import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { Modal, Spinner, Alert, Form, Row, Col } from 'react-bootstrap';
import './PerformanceChartModal.css';
import {
  Chart,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  LineController,
  BarController
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ThemeContext } from '../contexts/ThemeContext';
import { createLogger } from '../utils/debugLogger';
import { getAccountHistory, getAggregatedAccountHistory } from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { formatBalanceDisplay } from '../utils/formatBalance';

// LineController/BarController (not just LineElement/BarElement) are
// required here, not optional -- this component's own chartType toggle
// constructs `new Chart(ctx, { type: chartType, ... })` for both 'line'
// and 'bar'. Omitting them meant this only worked by luck: Chart.js's
// registry is a single global singleton, so as long as some OTHER chart
// component (BalanceHistoryModal, EdgeHistoryModal, etc.) happened to
// register them first in the page's module graph, this component rode on
// that side effect. Isolate this component (code-split route, module
// load order change) and `new Chart()` throws "bar/line is not a
// registered controller."
Chart.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  LineController,
  BarController,
  ChartDataLabels
);

const logger = createLogger(
  'performanceChart',
  'Performance change analytics modal with configurable cadence',
  false,
  'INFO'
);

const DEFAULT_PERIOD = 'ytd';
const DEFAULT_UNIT = 'month';
const DEFAULT_CHART_TYPE = 'bar';

const UNIT_OPTIONS = [
  { value: 'month', label: 'Month', durationDays: 30 },
  { value: 'week', label: 'Week', durationDays: 7 },
  { value: 'day', label: 'Day', durationDays: 1 }
];

const PERIOD_OPTIONS = [
  { value: 'ytd', label: 'Year to Date' },
  { value: '1month', label: '1 Month', durationDays: 30 },
  { value: '3months', label: '3 Months', durationDays: 90 },
  { value: '6months', label: '6 Months', durationDays: 180 },
  { value: '1year', label: '1 Year', durationDays: 365 },
  { value: '3years', label: '3 Years', durationDays: 365 * 3 },
  { value: 'alltime', label: 'All Time', durationDays: Number.POSITIVE_INFINITY }
];

const CHART_TYPE_OPTIONS = [
  { value: 'line', label: 'Line' },
  { value: 'bar', label: 'Bar' }
];

const formatChangeAmount = (amount) => {
  const rounded = Math.round(amount);
  const formatted = formatBalanceDisplay(rounded);
  return formatted.replace(/^\$-/, '-$').replace(/\.00$/, '');
};

const getPeriodDurationDays = (periodValue) => {
  const now = new Date();
  switch (periodValue) {
    case 'ytd': {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const diffMs = now.getTime() - startOfYear.getTime();
      return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
    }
    case '1month':
      return 30;
    case '3months':
      return 90;
    case '6months':
      return 180;
    case '1year':
      return 365;
    case '3years':
      return 365 * 3;
    case 'alltime':
      return Number.POSITIVE_INFINITY;
    default:
      return 30;
  }
};

const getUnitDurationDays = (unitValue) => {
  const unitOption = UNIT_OPTIONS.find(option => option.value === unitValue);
  return unitOption ? unitOption.durationDays : 1;
};

const isSelectionValid = (periodValue, unitValue) => {
  const periodDays = getPeriodDurationDays(periodValue);
  const unitDays = getUnitDurationDays(unitValue);

  if (!Number.isFinite(periodDays)) {
    return true;
  }

  return periodDays > unitDays;
};

const parseHistoryDate = (isoDate) => {
  const dateParts = isoDate.split('-');
  const year = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10) - 1;
  const day = parseInt(dateParts[2], 10);
  return new Date(year, month, day);
};

const getWeekKey = (date) => {
  const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = tempDate.getUTCDay() || 7;
  tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((tempDate - yearStart) / (1000 * 60 * 60 * 24) + 1) / 7);
  return {
    key: `${tempDate.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`,
    label: `Week ${weekNo}, ${tempDate.getUTCFullYear()}`
  };
};

const buildPerformanceSeries = (historyData, unit) => {
  if (!historyData || historyData.length === 0) {
    return [];
  }

  const sortedHistory = [...historyData].sort((a, b) => a.dateObj - b.dateObj);

  if (unit === 'day') {
    const mostRecentByDay = new Map();
    sortedHistory.forEach(item => {
      const dateKey = `${item.dateObj.getFullYear()}-${String(item.dateObj.getMonth() + 1).padStart(2, '0')}-${String(item.dateObj.getDate()).padStart(2, '0')}`;
      const existing = mostRecentByDay.get(dateKey);
      if (!existing || item.dateObj > existing.dateObj) {
        mostRecentByDay.set(dateKey, item);
      }
    });

    const dedupedHistory = Array.from(mostRecentByDay.values())
      .sort((a, b) => a.dateObj - b.dateObj);

    const points = [];
    for (let i = 1; i < dedupedHistory.length; i += 1) {
      const previous = dedupedHistory[i - 1];
      const current = dedupedHistory[i];
      const absoluteChange = current.totalValue - previous.totalValue;
      const change = previous.totalValue > 0
        ? (absoluteChange / previous.totalValue) * 100
        : 0;
      points.push({
        label: formatDate(current.dateObj),
        value: Number(change.toFixed(2)),
        absoluteChange
      });
    }
    return points;
  }

  const groups = new Map();

  sortedHistory.forEach(item => {
    const { dateObj, totalValue } = item;
    let groupKey;
    let displayLabel;

    if (unit === 'month') {
      groupKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      displayLabel = `${dateObj.toLocaleString('default', { month: 'short' })} ${dateObj.getFullYear()}`;
    } else if (unit === 'week') {
      const { key, label } = getWeekKey(dateObj);
      groupKey = key;
      displayLabel = label;
    } else {
      groupKey = dateObj.toISOString();
      displayLabel = formatDate(dateObj);
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        label: displayLabel,
        startValue: totalValue,
        endValue: totalValue,
        startDate: dateObj,
        endDate: dateObj
      });
    } else {
      const group = groups.get(groupKey);
      group.endValue = totalValue;
      if (dateObj < group.startDate) {
        group.startDate = dateObj;
        group.startValue = totalValue;
      }
      if (dateObj > group.endDate) {
        group.endDate = dateObj;
        group.endValue = totalValue;
      }
    }
  });

  const points = Array.from(groups.values())
    .sort((a, b) => a.startDate - b.startDate)
    .map(group => {
      const { startValue, endValue, label } = group;
      const absoluteChange = endValue - startValue;
      const change = startValue > 0 ? (absoluteChange / startValue) * 100 : 0;
      return {
        label,
        value: Number(change.toFixed(2)),
        absoluteChange
      };
    });

  return points;
};

function PerformanceChartModal({
  show,
  onHide,
  accountId,
  accountName,
  portfolioName,
  title,
  isAggregated = false,
  currentBalance = null,
  historyParams = {},
  isBalanceLoading = false,
}) {
  const { effectiveTheme } = useContext(ThemeContext);
  const chartCanvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const activeRequestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [chartPoints, setChartPoints] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(DEFAULT_PERIOD);
  const [selectedUnit, setSelectedUnit] = useState(DEFAULT_UNIT);
  const [chartType, setChartType] = useState(DEFAULT_CHART_TYPE);
  const [validationMessage, setValidationMessage] = useState('');

  const resolvedTitle = title
    || (isAggregated
      ? `${portfolioName ? `${portfolioName} ` : ''}Performance`
      : `${accountName ? `${accountName} ` : 'Account '}Performance`);

  const historyParamsString = useMemo(() => JSON.stringify(historyParams || {}), [historyParams]);

  useEffect(() => {
    if (!show) {
      activeRequestRef.current += 1;
      setHistoryData([]);
      setChartPoints([]);
      setError('');
      setLoading(false);
      setValidationMessage('');
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return;
    }

    if (!isAggregated && !accountId) {
      logger.warn('Performance chart requested without an account id.');
      setError('No account selected for performance view.');
      return;
    }

    const fetchPerformanceData = async () => {
      const requestId = activeRequestRef.current + 1;
      activeRequestRef.current = requestId;

      setLoading(true);
      setError('');
      setChartPoints([]);
      setHistoryData([]);

      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      try {
        const params = {
          period: selectedPeriod,
          ...JSON.parse(historyParamsString)
        };

        const response = isAggregated
          ? await getAggregatedAccountHistory(params)
          : await getAccountHistory(accountId, params);

        const rawHistory = response.data?.history || [];
        const filteredHistory = rawHistory
          .map(item => ({
            dateObj: parseHistoryDate(item.date),
            totalValue: item.total_value
          }))
          .filter(item => {
            const day = item.dateObj.getDay();
            return day !== 0 && day !== 6;
          });

        if (activeRequestRef.current === requestId) {
          setHistoryData(filteredHistory);
        }
      } catch (err) {
        logger.error('Failed to load performance history', err);
        if (activeRequestRef.current === requestId) {
          setError(err.response?.data?.error || 'Unable to load performance data.');
          setLoading(false);
        }
      }
    };

    fetchPerformanceData();
  }, [show, isAggregated, accountId, selectedPeriod, historyParamsString]);

  useEffect(() => {
    if (!show) {
      setChartPoints([]);
      setLoading(false);
      return;
    }

    if (isBalanceLoading) {
      setLoading(true);
      setChartPoints([]);
      return;
    }

    if (!historyData.length) {
      setChartPoints([]);
      setLoading(false);
      return;
    }

    const workingData = [...historyData];

    if (currentBalance !== null && historyData.length > 0) {
      const lastEntry = historyData[historyData.length - 1];
      if (Math.abs((currentBalance || 0) - lastEntry.totalValue) > 0.01) {
        workingData.push({
          dateObj: new Date(),
          totalValue: currentBalance
        });
      }
    }

    const points = buildPerformanceSeries(workingData, selectedUnit);
    setChartPoints(points);
    setLoading(false);
  }, [historyData, selectedUnit, currentBalance, show, isBalanceLoading]);

  useEffect(() => {
    if (!show || loading || error || !chartPoints.length) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      return;
    }

    const ctx = chartCanvasRef.current?.getContext('2d');
    if (!ctx) {
      return;
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    if (effectiveTheme === 'dark') {
      gradient.addColorStop(0, 'rgba(77, 171, 247, 0.35)');
      gradient.addColorStop(1, 'rgba(13, 110, 253, 0.05)');
    } else {
      gradient.addColorStop(0, 'rgba(13, 110, 253, 0.35)');
      gradient.addColorStop(1, 'rgba(13, 110, 253, 0.05)');
    }

    const datasetValues = chartPoints.map(point => point.value);
    const barColors = datasetValues.map(value => (value >= 0 ? '#198754' : '#dc3545'));
    const shouldShowPercentLabels = selectedUnit !== 'day';
    const shouldShowAmountLabels = selectedUnit !== 'week' && selectedUnit !== 'day';

    const datasetBase = {
      label: 'Performance Change (%)',
      data: datasetValues,
      datalabels: {
        align: 'end',
        anchor: 'end'
      }
    };

    const dataset = chartType === 'line'
      ? {
        ...datasetBase,
        backgroundColor: gradient,
        borderColor: effectiveTheme === 'dark' ? '#4dabf7' : '#0d6efd',
        fill: true,
        tension: 0.35,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: effectiveTheme === 'dark' ? '#4dabf7' : '#0d6efd',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 1
      }
      : {
        ...datasetBase,
        backgroundColor: barColors,
        borderColor: barColors,
        borderRadius: 6,
        borderSkipped: false
      };

    chartInstanceRef.current = new Chart(ctx, {
      type: chartType,
      data: {
        labels: chartPoints.map(point => point.label),
        datasets: [dataset]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 450
        },
        layout: {
          padding: {
            top: 36,
            bottom: 16
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          x: {
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              color: effectiveTheme === 'dark' ? '#e9ecef' : '#495057'
            },
            grid: {
              color: effectiveTheme === 'dark' ? 'rgba(233, 236, 239, 0.08)' : 'rgba(0,0,0,0.05)'
            }
          },
          y: {
            ticks: {
              callback: value => `${value}%`,
              color: effectiveTheme === 'dark' ? '#e9ecef' : '#495057'
            },
            grid: {
              color: effectiveTheme === 'dark' ? 'rgba(233, 236, 239, 0.08)' : 'rgba(0,0,0,0.05)'
            },
            title: {
              display: true,
              text: 'Change (%)',
              color: effectiveTheme === 'dark' ? '#e9ecef' : '#495057'
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const point = chartPoints[context.dataIndex];
                const percentText = `${context.parsed.y.toFixed(2)}%`;
                if (!point) {
                  return ` ${percentText}`;
                }
                if (!shouldShowAmountLabels) {
                  return ` ${percentText}`;
                }
                const amountText = formatChangeAmount(point.absoluteChange);
                return ` ${percentText} ${amountText}`;
              },
              labelTextColor: (context) => (context.parsed.y >= 0 ? '#198754' : '#dc3545')
            }
          },
          datalabels: {
            display: () => shouldShowPercentLabels,
            formatter: (value, context) => {
              if (!shouldShowPercentLabels) {
                return '';
              }
              const point = chartPoints[context.dataIndex];
              const absoluteChange = point?.absoluteChange || 0;
              if (Math.abs(value) < 0.5 && Math.abs(absoluteChange) < 0.5) {
                return '';
              }
              const percentText = `${value.toFixed(1)}%`;
              if (!point) {
                return percentText;
              }
              if (!shouldShowAmountLabels) {
                return percentText;
              }
              const amountText = formatChangeAmount(point.absoluteChange);
              return `${percentText}\n${amountText}`;
            },
            color: (context) => {
              const datasetValue = context.dataset.data[context.dataIndex];
              return datasetValue >= 0 ? '#198754' : '#dc3545';
            },
            font: () => ({
              size: chartType === 'bar' ? 14 : 13,
              weight: '700'
            }),
            textAlign: 'center',
            align: (context) => {
              const datasetValue = context.dataset.data[context.dataIndex];
              if (chartType === 'bar') {
                return datasetValue >= 0 ? 'center' : 'start';
              }
              return datasetValue >= 0 ? 'end' : 'start';
            },
            anchor: (context) => {
              const datasetValue = context.dataset.data[context.dataIndex];
              if (chartType === 'bar') {
                return datasetValue >= 0 ? 'center' : 'start';
              }
              return datasetValue >= 0 ? 'end' : 'start';
            },
            offset: (context) => {
              const datasetValue = context.dataset.data[context.dataIndex];
              if (chartType === 'bar') {
                return datasetValue >= 0 ? 0 : 6;
              }
              return datasetValue >= 0 ? -6 : 6;
            },
            padding: {
              top: 6,
              bottom: 6
            },
            clamp: true,
            clip: false
          }
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [chartPoints, show, loading, error, effectiveTheme, chartType, selectedUnit]);

  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    if (!isSelectionValid(newPeriod, selectedUnit)) {
      setValidationMessage('Period must be longer than the unit. Select a longer period or a smaller unit.');
      return;
    }
    setValidationMessage('');
    setSelectedPeriod(newPeriod);
  };

  const handleUnitChange = (event) => {
    const newUnit = event.target.value;
    if (!isSelectionValid(selectedPeriod, newUnit)) {
      setValidationMessage('Period must be longer than the unit. Select a longer period or a smaller unit.');
      return;
    }
    setValidationMessage('');
    setSelectedUnit(newUnit);
  };

  const handleChartTypeChange = (value) => {
    setChartType(value);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      dialogClassName="performance-chart-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{resolvedTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form className="mb-3">
          <Row className="gy-3 align-items-center performance-config-row">
            <Col xs={12} md={8}>
              <div className="performance-config-pair">
                <Form.Group
                  controlId="performancePeriod"
                  className="performance-select-group"
                >
                  <Form.Label className="small fw-semibold mb-0">Period</Form.Label>
                  <Form.Select value={selectedPeriod} onChange={handlePeriodChange}>
                    {PERIOD_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group
                  controlId="performanceUnit"
                  className="performance-select-group"
                >
                  <Form.Label className="small fw-semibold mb-0">Unit</Form.Label>
                  <Form.Select value={selectedUnit} onChange={handleUnitChange}>
                    {UNIT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </Col>
            <Col xs={12} md="auto" className="ms-md-auto">
              <Form.Group
                controlId="performanceChartType"
                className="chart-type-toggle-group"
              >
                <Form.Label
                  id="performanceChartTypeLabel"
                  className="small fw-semibold mb-0"
                >
                  Chart Type
                </Form.Label>
                <div
                  className="chart-type-toggle"
                  role="radiogroup"
                  aria-labelledby="performanceChartTypeLabel"
                >
                  {CHART_TYPE_OPTIONS.map(option => {
                    const isActive = chartType === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`chart-type-toggle__option${isActive ? ' is-active' : ''}`}
                        onClick={() => handleChartTypeChange(option.value)}
                        role="radio"
                        aria-checked={isActive}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </Form.Group>
            </Col>
          </Row>
          {validationMessage && (
            <Form.Text className="text-danger">{validationMessage}</Form.Text>
          )}
        </Form>

        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 360 }}>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading performance data...</span>
            </Spinner>
          </div>
        )}

        {!loading && !error && chartPoints.length === 0 && (
          <Alert variant="info" className="mb-0">
            Not enough historical data is available for the selected configuration.
          </Alert>
        )}

        {!loading && !error && chartPoints.length > 0 && (
          <div style={{ height: 540 }}>
            <canvas ref={chartCanvasRef} aria-label="Performance trend chart" />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default PerformanceChartModal;
